import React, { useEffect, useContext, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';
import { useMediaQuery } from 'react-responsive';

import useScrollSticky from '../../_shared/hooks/useScrollSticky';
import Filter from './Filter';
import { ListVariant } from '../../_shared/types/trade';
import isDescendantOfClass from '../../_shared/utils/isDescendantOfClass';
import useOutsideClickListener from '../../_shared/hooks/useOutsideClickListener';
import { updateShowFilters } from '../../redux/slices/ui/layout';

import { StyledFilterContainer } from './Styles';

const propTypes = {
  userLists: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.array])
  ).isRequired,
  tradeState: PropTypes.number.isRequired,
  companyId: PropTypes.number,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  filtersButtonRef: PropTypes.shape({ current: PropTypes.elementType }),
  isFilterReset: PropTypes.bool.isRequired,
  setIsFilterReset: PropTypes.func.isRequired,
  filterPillName: PropTypes.string
};

const defaultProps = {
  companyId: undefined,
  filtersButtonRef: null,
  filterPillName: ''
};

const Filters = ({
  userLists,
  tradeState,
  companyId,
  isEditing,
  setIsEditing,
  filtersButtonRef,
  refetchFilters,
  isFilterReset,
  setIsFilterReset,
  filterPillName,
  isFiltersListFetched
}) => {
  const filtersRef = useRef(null);
  const [listVariant, showFilters] = useSelector(
    state => [state?.ui?.layout?.listVariant, state?.ui?.layout?.showFilters],
    shallowEqual
  );
  const dispatch = useDispatch();
  const themeContext = useContext(ThemeContext);
  const isLargeScreen = useMediaQuery({
    query: `(min-width: ${themeContext.BP.GRIDVIEW_DESKTOP_WM})`
  });

  useScrollSticky(filtersRef);

  const onClickOutsideFilters = event => {
    // additional checks to make sure filters are left open if user clicks on the popover or portal
    if (
      !isDescendantOfClass(event.target, 'react-tiny-popover-container') &&
      !isDescendantOfClass(event.target, 'ReactModalPortal')
    ) {
      // popover also has onClickOutside event, we want that to close that first if it's open
      setTimeout(() => {
        dispatch(updateShowFilters(false));
      }, 100);
    }
  };

  // activate listener only when the filters are open in small screen grid view
  useOutsideClickListener(
    filtersRef,
    onClickOutsideFilters,
    listVariant === ListVariant.GRID && showFilters && !isLargeScreen,
    filtersButtonRef
  );

  useEffect(() => {
    dispatch(
      updateShowFilters(listVariant === ListVariant.LIST || isLargeScreen)
    );
  }, [dispatch, isLargeScreen, listVariant]);

  return (
    <StyledFilterContainer
      variant={listVariant}
      visible={showFilters}
      ref={filtersRef}
    >
      <Filter
        userLists={userLists}
        tradeState={tradeState}
        companyId={companyId}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        filtersRef={filtersRef}
        refetchFilters={refetchFilters}
        isFilterReset={isFilterReset}
        setIsFilterReset={setIsFilterReset}
        filterPillName={filterPillName}
        isFiltersListFetched={isFiltersListFetched}
      />
    </StyledFilterContainer>
  );
};

Filters.propTypes = propTypes;
Filters.defaultProps = defaultProps;

export default Filters;
